import { useParams, Navigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Clock, Calendar, ArrowLeft, Share2, Twitter, Facebook, Linkedin, User } from "lucide-react";
import { fetchBlog, fetchBlogs } from "../../api/public.api.js";
import { assetUrl } from "../../api/client.js";
import { imageOrPlaceholder, imgErrorFallback } from "../../lib/placeholders.js";
import { Container } from "../../components/layout/Container.jsx";
import { Breadcrumbs } from "../../components/shared/PageHero.jsx";
import { RichText } from "../../components/shared/RichText.jsx";
import { SEO } from "../../lib/seo.jsx";
import { formatDate, formatRelativeTime } from "../../lib/format.js";

export default function BlogDetailPage() {
  const { slug } = useParams();
  const {
    data: post,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["public", "blog", slug],
    queryFn: () => fetchBlog(slug),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const relatedQ = useQuery({
    queryKey: ["public", "blog", "related", post?.category?.slug, post?._id],
    queryFn: () =>
      fetchBlogs({
        limit: 4,
        ...(post?.category?.slug && { category: post.category.slug }),
      }),
    enabled: Boolean(post?.category?.slug),
    staleTime: 5 * 60_000,
  });
  const related = (relatedQ.data?.items ?? []).filter((p) => p._id !== post?._id).slice(0, 3);

  if (isError) return <Navigate to="/blog" replace />;
  if (isLoading || !post) {
    return (
      <Container className="py-20">
        <div className="h-[60vh] animate-pulse rounded-2xl bg-bg-elevated" />
      </Container>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    image: post.heroImage ? assetUrl(post.heroImage) : undefined,
    datePublished: post.publishedAt,
    dateModified: post.updatedAt ?? post.publishedAt,
    author: { "@type": "Person", name: post.author ?? "Shere-e-Punjab Homes" },
    publisher: { "@type": "Organization", name: "Shere-e-Punjab Homes" },
    description: post.excerpt,
  };

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareTitle = encodeURIComponent(post.title);
  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?text=${shareTitle}&url=${encodeURIComponent(shareUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
  };

  const heroKey = post.slug ?? post._id ?? post.title;

  return (
    <>
      <SEO
        title={post.title}
        description={post.excerpt}
        image={post.heroImage}
        type="article"
        schema={articleSchema}
      />

      <article>
        {/* Cinematic hero — title overlays the image directly */}
        <div className="relative isolate -mt-16 lg:-mt-20">
          <div className="absolute inset-0 -z-10 overflow-hidden">
            <img
              src={imageOrPlaceholder(post.heroImage, { pool: "blog", key: heroKey })}
              onError={imgErrorFallback(heroKey)}
              alt=""
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-bg-base via-bg-base/70 to-bg-base/30" />
            <div className="absolute inset-0 bg-gradient-to-r from-bg-base/60 via-transparent to-transparent" />
          </div>

          <Container className="flex min-h-[68vh] flex-col justify-end pb-14 pt-32 lg:min-h-[78vh] lg:pb-20 lg:pt-40">
            <div className="max-w-3xl">
              {post.category?.name && (
                <Link
                  to={`/blog/category/${post.category.slug}`}
                  className="inline-flex rounded-full bg-accent/15 px-3 py-1 text-xs font-medium uppercase tracking-widest text-accent backdrop-blur"
                >
                  {post.category.name}
                </Link>
              )}
              <h1 className="mt-5 font-display text-4xl leading-[1.1] text-text-primary lg:text-6xl">
                {post.title}
              </h1>
              {post.excerpt && (
                <p className="mt-5 max-w-2xl text-lg text-text-secondary">{post.excerpt}</p>
              )}
              <div className="mt-7 flex flex-wrap items-center gap-4 text-sm text-text-muted">
                {post.author && (
                  <span className="inline-flex items-center gap-1.5 text-text-secondary">
                    <User className="h-3.5 w-3.5 text-accent" /> {post.author}
                  </span>
                )}
                {post.publishedAt && (
                  <>
                    <span aria-hidden>·</span>
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5 text-accent" />
                      {formatDate(post.publishedAt)}
                    </span>
                  </>
                )}
                {post.readingTime && (
                  <>
                    <span aria-hidden>·</span>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5 text-accent" />
                      {post.readingTime} min read
                    </span>
                  </>
                )}
              </div>
            </div>
          </Container>
        </div>

        <Container className="py-12 lg:py-16">
          <Breadcrumbs
            items={[
              { label: "Blog", to: "/blog" },
              ...(post.category?.name
                ? [{ label: post.category.name, to: `/blog/category/${post.category.slug}` }]
                : []),
              { label: post.title },
            ]}
            className="mb-10"
          />

          <div className="mx-auto max-w-3xl">
            <RichText html={post.body} />
          </div>

          {/* Tags + share */}
          <div className="mx-auto mt-12 flex max-w-3xl flex-wrap items-center justify-between gap-4 border-y border-border-subtle py-6">
            <div className="flex flex-wrap gap-2">
              {post.tags?.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-border-subtle bg-bg-elevated px-3 py-1 text-xs text-text-secondary"
                >
                  #{tag}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 text-xs text-text-muted">
                <Share2 className="h-3.5 w-3.5" /> Share
              </span>
              <ShareIcon href={shareLinks.twitter} icon={Twitter} label="Share on Twitter" />
              <ShareIcon href={shareLinks.facebook} icon={Facebook} label="Share on Facebook" />
              <ShareIcon href={shareLinks.linkedin} icon={Linkedin} label="Share on LinkedIn" />
            </div>
          </div>

          {/* Related posts */}
          {related.length > 0 && (
            <section className="mx-auto mt-20 max-w-5xl">
              <h2 className="font-display text-3xl text-text-primary">
                More from {post.category?.name ?? "the blog"}
              </h2>
              <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
                {related.map((p) => {
                  const k = p.slug ?? p._id ?? p.title;
                  return (
                    <Link
                      key={k}
                      to={`/blog/${p.slug}`}
                      className="group flex h-full flex-col overflow-hidden rounded-2xl border border-border-subtle bg-bg-elevated transition-all hover:-translate-y-0.5 hover:border-accent/60"
                    >
                      <div className="relative aspect-[16/10] overflow-hidden bg-bg-elevated-2">
                        <img
                          src={imageOrPlaceholder(p.heroImage, { pool: "blog", key: k })}
                          onError={imgErrorFallback(k)}
                          alt={p.title}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="flex flex-1 flex-col p-5">
                        <p className="text-xs text-text-muted">
                          {formatRelativeTime(p.publishedAt)}
                        </p>
                        <h3 className="mt-2 line-clamp-2 font-display text-base text-text-primary group-hover:text-accent">
                          {p.title}
                        </h3>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          <div className="mx-auto mt-14 max-w-3xl">
            <Link
              to="/blog"
              className="inline-flex items-center gap-1 text-sm text-text-muted hover:text-accent"
            >
              <ArrowLeft className="h-3 w-3" /> Back to blog
            </Link>
          </div>
        </Container>
      </article>
    </>
  );
}

function ShareIcon({ href, icon: Icon, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="flex h-9 w-9 items-center justify-center rounded-full border border-border-subtle text-text-secondary transition-colors hover:border-accent hover:bg-accent/10 hover:text-accent"
    >
      <Icon className="h-4 w-4" />
    </a>
  );
}